import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import GiveawayCard from '../components/GiveawayCard';
import { Button } from '../components/ui/button';
import { 
  MapPin, Users, Gift, Trophy, TrendingUp, Building, 
  ArrowRight, Star, CheckCircle, ChevronRight 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CityPage = () => {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCity();
  }, [slug]);

  const fetchCity = async () => {
    try {
      const response = await axios.get(`${API}/cities/${slug}`);
      setCity(response.data);
    } catch (error) {
      console.error('Error fetching city:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="pt-32 text-center">
          <MapPin className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">City Not Found</h2>
          <p className="text-neutral-500 mb-6">This city hasn't been launched yet.</p>
          <Link to="/giveaways">
            <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl">
              Browse All Giveaways
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 relative overflow-hidden">
        {city.featured_image && (
          <div className="absolute inset-0 opacity-20">
            <img src={city.featured_image} alt={city.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#FF3B30]/20 text-[#FF3B30] font-medium px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" />
              {city.province_state}, {city.country}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Home Giveaways in <span className="text-[#FF3B30]">{city.name}</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
              Enter for free and win amazing home upgrades sponsored by top local realtors in {city.name}
            </p>
            
            {/* City Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{city.giveaways?.length || 0}</p>
                <p className="text-white/60">Active Giveaways</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{city.sponsors?.length || 0}</p>
                <p className="text-white/60">Local Sponsors</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{city.total_leads || 0}</p>
                <p className="text-white/60">Homeowners Joined</p>
              </div>
            </div>

            <Link to="/giveaways">
              <Button className="h-14 px-8 bg-[#FF3B30] hover:bg-[#DC2626] text-white text-lg font-medium rounded-xl" data-testid="enter-city-giveaways">
                Enter Giveaways in {city.name}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Active Giveaways */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Active Now</p>
              <h2 className="text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
                Giveaways in {city.name}
              </h2>
            </div>
          </div>

          {city.giveaways?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="city-giveaways">
              {city.giveaways.map((giveaway, index) => (
                <motion.div
                  key={giveaway.giveaway_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <GiveawayCard giveaway={giveaway} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-neutral-50 rounded-2xl">
              <Gift className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-600 mb-2">Coming Soon to {city.name}</h3>
              <p className="text-neutral-500">Be the first to know when giveaways launch here!</p>
            </div>
          )}
        </div>
      </section>

      {/* City Leaderboard */}
      {city.leaderboard?.length > 0 && (
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
            <div className="text-center mb-8">
              <p className="text-[#FF3B30] font-semibold uppercase tracking-wider text-sm mb-2">Sponsor Rankings</p>
              <h2 className="text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
                Top Sponsors in {city.name}
              </h2>
            </div>

            <div className="space-y-4" data-testid="city-leaderboard">
              {city.leaderboard.map((sponsor, index) => (
                <motion.div
                  key={sponsor.profile_id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-white rounded-2xl p-5 border ${
                    index === 0 ? 'border-amber-300 shadow-md' : 'border-neutral-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl ${
                      index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white' :
                      index === 1 ? 'bg-gradient-to-br from-neutral-300 to-neutral-400 text-white' :
                      index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                      'bg-neutral-100 text-neutral-600'
                    }`}>
                      {sponsor.rank}
                    </div>
                    <img
                      src={sponsor.profile_image || 'https://images.pexels.com/photos/7578976/pexels-photo-7578976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                      alt={sponsor.company_name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-neutral-900">{sponsor.company_name}</h3>
                      <p className="text-sm text-neutral-500 capitalize">{sponsor.business_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-neutral-900">{sponsor.total_leads || 0}</p>
                      <p className="text-sm text-neutral-500">Leads</p>
                    </div>
                    {sponsor.badge && (
                      <div className="hidden sm:flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                        <Trophy className="w-4 h-4" />
                        Top Sponsor
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsor CTA */}
      <section className="py-16 bg-[#FF3B30]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            Are You a Realtor in {city.name}?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Become a sponsor and get exclusive access to high-intent homeowner leads in your area.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/sponsor-signup">
              <Button className="h-14 px-8 bg-white text-[#FF3B30] hover:bg-neutral-100 text-lg font-medium rounded-xl">
                Become a Sponsor
              </Button>
            </Link>
            <Link to="/sponsors">
              <Button variant="outline" className="h-14 px-8 border-white text-white hover:bg-white/10 text-lg font-medium rounded-xl">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
